import "./SignUp.css";
import HeaderImage from "../login/img/loginLogo.png";
import Title from "../../component/title/Title";
import TextInput from "../../component/textInput/TextInput";
import React, { useState, useEffect } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useNavigate, useParams } from "react-router-dom";
import asiamobiliti from "./img/asiamobiliti.png";
import { PrimaryButton } from "../../component/buttonComponent/ButtonComponent";
import PasswordStrengthBar from "../../component/passwordStrengthBar/PasswordStrengthBar";
import CustomPhoneInput from "../../component/customPhoneInput/CustomPhoneInput";
import CryptoJS from "crypto-js";
import { removePlusPhone } from "../../config/utilities/formatPhone";
import SnackBar from "../../component/Snackbar/Snackbar";
import Cookies from "js-cookie";
import { APISignUp } from "../../config/utilities/APIs";

const emailKey = process.env.REACT_APP_EMAIL_SECRET_KEY;

export default function SignUp() {
  const [input, setInput] = useState({
    email: "",
    phone: "",
    password: "",
    passwordVerify: "",
  });

  const [error, setError] = useState({
    password: false,
    phoneNumber: false,
    passwordVerify: false,
  });

  const [keyUp, setKeyUp] = useState("", false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");

  const navigate = useNavigate();

  const params = useParams();
  const token = params["*"];

  const decryptToken = (encryptedToken) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, emailKey);
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

      return decryptedData;
    } catch (error) {
      console.error("Decryption failed: ", error.message);
      return null;
    }
  };

  useEffect(() => {
    const userData = token ? decryptToken(token) : null;

    if (!userData || new Date(userData.linkExpireAt) < new Date()) {
      navigate("/invalidlink", { replace: true });
    } else {
      setInput((prevInput) => ({
        ...prevInput,
        email: userData.emailAddress,
      }));
    }
  }, [token, navigate]);

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }
  const [windowSize, setWindowSize] = useState(getWindowSize());
  const isMobileViewport = windowSize.innerWidth <= 768;

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
    }

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const passwordOnChangeFunction = (e) => {
    setInput({ ...input, password: e.target.value });
  };

  const handlePhoneChange = (phone) => {
    if (typeof phone === "string") {
      setInput((prev) => ({ ...prev, phone }));
    }
  };

  const verifyPasswordOnChangeFunction = (e) => {
    setInput({ ...input, passwordVerify: e.target.value });
  };

  const handleStrengthChange = (strength) => {
    setPasswordStrength(strength);
  };

  const btnSignUp = async () => {
    setLoading(true);

    const formattedPhone = removePlusPhone(input.phone);

    const adminData = {
      email: input.email,
      phoneNumber: formattedPhone,
      password: input.passwordVerify,
    };

    try {
      const response = await APISignUp(adminData, setLoading);

      if (response.status === 200) {
        Cookies.set("verificationEmail", input.email, {
          expires: 1,
          path: "/",
        });
        navigate("/verificationreceived", { replace: true });
      } else {
        throw new Error(
          response.response?.data?.message || "Failed to sign up"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Sign Up Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    let isValid = true;

    // Validate fields without showing errors for empty inputs
    const newErrors = {
      phone: input.phone.trim() !== "" && !isValidPhoneNumber(input.phone),
      passwordVerify:
        input.passwordVerify.trim() !== "" &&
        input.password !== input.passwordVerify,
    };

    // Determine overall validity
    isValid =
      !Object.values(newErrors).includes(true) &&
      input.phone.trim() !== "" &&
      input.password.trim() !== "" &&
      input.passwordVerify.trim() !== "" &&
      passwordStrength >= 100;

    // Set errors in state (excluding password)
    setError(newErrors);

    return isValid;
  };

  useEffect(() => {
    setIsSubmitDisabled(!isFormValid());
  }, [input, passwordStrength]);

  const fullWidth = "100%";
  const fieldWidth = isMobileViewport ? fullWidth : "22.5rem";
  const phoneWidth = isMobileViewport ? fullWidth : "22.6rem";
  const passwordWidth = isMobileViewport ? fullWidth : "20.7rem";

  return (
    <>
      <div
        className="signup-roww"
        style={{ minHeight: windowSize.innerHeight, width: windowSize.innerWidth }}
      >
        <div className="signup-column">
          <div className="signup-container-main">
            <div className="signup-section">
              <div className="signup-container">
                <div className="signup-contents">
                  <div className="signup-logo" style={{ marginBottom: "1rem" }}>
                    <img
                      loading="lazy"
                      src={HeaderImage}
                      className="logo"
                      alt="header-logo"
                    />
                  </div>

                  <div className="signup-header-form">
                    <div className="signup-header">
                      <Title
                        title="Sign Up"
                        subtitle={`Welcome ${input.email}!`}
                        titleSize="2.235rem"
                        subtitleSize="1rem"
                        gap="0.75rem"
                      />
                    </div>
                    <div className="signup-content-form">
                      <div className="signup-form">
                        <div className="signup-input-container">
                          <CustomPhoneInput
                            width={phoneWidth}
                            label="Phone Number"
                            value={input.phone || ""}
                            onChange={handlePhoneChange}
                            error={error.phone}
                            errorMessage="Enter valid phone number"
                          />
                        </div>
                        <div className="signup-input-container">
                          <TextInput
                            width={passwordWidth}
                            label="Password"
                            placeholder="Create a password"
                            setInput={setInput}
                            input={input}
                            name="password"
                            type="password"
                            error={error.password}
                            setError={setError}
                            keyUp={keyUp}
                            setKeyUp={setKeyUp}
                            onChangeFunction={passwordOnChangeFunction}
                          />
                        </div>
                        <div className="signup-input-container">
                          <TextInput
                            label="Verify Password"
                            placeholder="Verify password"
                            setInput={setInput}
                            input={input}
                            name="passwordVerify"
                            type="password"
                            error={error.passwordVerify}
                            errorMessage={"Passwords do not match"}
                            setError={setError}
                            keyUp={keyUp}
                            setKeyUp={setKeyUp}
                            onChangeFunction={verifyPasswordOnChangeFunction}
                            width={passwordWidth}
                          />
                          <div className="signup-password-strength-bar">
                            <PasswordStrengthBar
                              module="signup"
                              password={input.password}
                              onStrengthChange={handleStrengthChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="signup-action">
                        <PrimaryButton
                          disable={isSubmitDisabled}
                          label="Sign Up"
                          width={fieldWidth}
                          onClickFunction={btnSignUp}
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="signup-footer">
            <Title
              subtitle="Copyright © 2025 Asia Mobility Technologies. All Rights Reserved."
              titleSize="0.875rem"
              subtitleSize="0.875rem"
              gap="0rem"
            />
          </div>
        </div>
        <div className="image-signup-section">
          <div className="just-signup-quote">
            <div className="signup-quote-container">
              <div className="signup-quote">
                All-in-one smart admin console for real-time tracking, route
                management, scheduling, and notifications.
              </div>
              <div className="text-and-stars">
                <div className="powered-by" style={{ gap: "0rem" }}>
                  <Title
                    title=""
                    subtitle="Powered by"
                    titleSize="0rem"
                    subtitleSize="1rem"
                    gap="0rem"
                  />
                  <img
                    src={asiamobiliti}
                    loading="lazy"
                    alt="Asia Mobiliti logo"
                    style={{
                      height: "1rem",
                      paddingTop: "0.2rem",
                      paddingLeft: "0.2rem",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </>
  );
}
